import { z, type RefinementCtx } from "zod";

/**
 * Schema for the `_provinces` meta field set by the form.
 * When populated, the province field becomes required and the value
 * must be one of the valid codes.
 */
const provincesMetaSchema = z
  .object({
    /** Label for the field, e.g. "State", "Region" */
    type: z.string(),
    /** Valid province codes for the selected country */
    codes: z.array(z.string())
  })
  .optional();

/**
 * Base address field definitions shared across all address forms.
 * These follow the Medusa v2 StoreCustomerAddress structure.
 *
 * `_provinces` is a meta field set by the form to carry the list of
 * valid provinces for the selected country. When set, the province
 * field becomes required and its value must match one of the codes.
 */
export const baseAddressFields = {
  first_name: z.string().min(1, "First name is required").max(50),
  last_name: z.string().min(1, "Last name is required").max(50),
  company: z.string().max(100).optional().or(z.literal("")),
  address_1: z.string().min(1, "Address is required").max(200),
  address_2: z.string().max(200).optional().or(z.literal("")),
  city: z.string().min(1, "City is required").max(100),
  postal_code: z.string().min(1, "Postcode is required").max(20),
  province: z.string().max(100).optional().or(z.literal("")),
  country_code: z.string().min(2, "Country is required").max(2),
  phone: z
    .string()
    .min(1, "Phone number is required")
    .regex(/^\+?[0-9\s\-()]+$/, "Invalid phone number format")
    .or(z.literal(""))
    .optional(),
  _provinces: provincesMetaSchema
};

/**
 * Zod superRefine callback: when `_provinces` is set, province is
 * required and must be one of the valid codes.
 */
export const provinceRefinement = (
  data: {
    _provinces?: { type: string; codes: string[] };
    province?: string;
  },
  ctx: RefinementCtx
) => {
  if (!data._provinces) return;

  const { type, codes } = data._provinces;

  if (!data.province || data.province === "") {
    ctx.addIssue({
      code: "custom",
      message: `${type} is required`,
      path: ["province"]
    });
    return;
  }

  if (!codes.includes(data.province)) {
    ctx.addIssue({
      code: "custom",
      message: `Invalid ${type.toLowerCase()} selected`,
      path: ["province"]
    });
  }
};

/**
 * Inline address schema — base fields without id or address_name.
 * Used for one-time (non-saved) addresses in checkout.
 */
export const inlineAddressSchema = z
  .object(baseAddressFields)
  .superRefine(provinceRefinement);

export type BaseAddressData = z.infer<typeof inlineAddressSchema>;

/**
 * Full address schema for saved customer addresses.
 * Includes id (for edit mode) and address_name (required).
 */
export const addressSchema = z
  .object({
    id: z.string().optional(),
    address_name: z.string().min(1, "Address name is required").max(100),
    ...baseAddressFields
  })
  .superRefine(provinceRefinement);

export type AddressFormData = z.infer<typeof addressSchema>;

/**
 * Default empty values for the base address fields
 */
export const baseEmptyAddressValues: BaseAddressData = {
  first_name: "",
  last_name: "",
  company: "",
  address_1: "",
  address_2: "",
  city: "",
  postal_code: "",
  country_code: "au",
  province: "",
  phone: "",
  _provinces: undefined
};

/**
 * Default empty values for the full address form (with id + address_name)
 */
export const emptyAddressFormValues: AddressFormData = {
  id: undefined,
  address_name: "",
  ...baseEmptyAddressValues
};
